// Aba "Skills" — lista as tools que o Dwight pode chamar com filtros
// (leitura/escrita, usadas/não-usadas) e detalhes de cada parâmetro.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Alert, Tag, Table, Typography, Space, Flex, Collapse,
} from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import type { Skill } from './types';

const { Text } = Typography;

export default function SkillsTab() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'todas' | 'leitura' | 'escrita' | 'usadas' | 'nao-usadas'>('todas');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/agentes/exemplos`,
          { action: 'listar_skills' }, { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setSkills(Array.isArray(data) ? data : []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtradas = skills.filter(s => {
    if (filtro === 'leitura') return s.tipo === 'leitura';
    if (filtro === 'escrita') return s.tipo === 'escrita';
    if (filtro === 'usadas') return s.stats_30d.chamadas > 0;
    if (filtro === 'nao-usadas') return s.stats_30d.chamadas === 0;
    return true;
  });
  filtradas.sort((a, b) => b.stats_30d.chamadas - a.stats_30d.chamadas);

  if (loading) return <Card loading />;

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 14 }}
        message={
          <Text style={{ fontSize: 12 }}>
            <strong>Skills (tools)</strong> que o Dwight pode chamar. <strong>Leitura</strong> roda
            automaticamente; <strong>Escrita</strong> vira card de aprovação. Stats dos últimos 30 dias.
          </Text>
        } />

      <Space wrap style={{ marginBottom: 14 }}>
        {[
          { id: 'todas', label: `Todas (${skills.length})` },
          { id: 'leitura', label: `Leitura (${skills.filter(s => s.tipo === 'leitura').length})` },
          { id: 'escrita', label: `Escrita (${skills.filter(s => s.tipo === 'escrita').length})` },
          { id: 'usadas', label: `Usadas em 30d (${skills.filter(s => s.stats_30d.chamadas > 0).length})` },
          { id: 'nao-usadas', label: `Não usadas (${skills.filter(s => s.stats_30d.chamadas === 0).length})` },
        ].map(f => (
          <Tag.CheckableTag key={f.id} checked={filtro === f.id} onChange={() => setFiltro(f.id as any)}>
            {f.label}
          </Tag.CheckableTag>
        ))}
      </Space>

      <Collapse
        size="small" accordion
        items={filtradas.map(s => ({
          key: s.nome,
          label: (
            <Flex align="center" gap={10}>
              <Text code style={{ fontWeight: 600, color: '#020D7F' }}>{s.nome}</Text>
              <Tag color={s.tipo === 'leitura' ? 'green' : 'gold'}>{s.tipo.toUpperCase()}</Tag>
              <Text type="secondary" style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.descricao}
              </Text>
              <Space size={10} style={{ fontSize: 10 }}>
                <Text type="secondary">
                  {s.stats_30d.chamadas > 0 ? `${s.stats_30d.chamadas}×/30d` : '— sem uso'}
                </Text>
                {s.stats_30d.erros > 0 && (
                  <Text style={{ color: '#B91626' }}>⚠ {s.stats_30d.taxa_erro_pct}%</Text>
                )}
              </Space>
            </Flex>
          ),
          children: (
            <div style={{ fontSize: 12 }}>
              <Text style={{ display: 'block', marginBottom: 10 }}>
                <strong>Descrição:</strong> {s.descricao}
              </Text>
              <Text strong style={{ fontSize: 11, color: '#020D7F', textTransform: 'uppercase' }}>
                Parâmetros aceitos:
              </Text>
              {s.campos.length === 0
                ? <Text type="secondary" italic style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                    Sem parâmetros (chamada direta).
                  </Text>
                : (
                  <Table size="small" pagination={false} rowKey="nome" style={{ marginTop: 6 }}
                    dataSource={s.campos}
                    columns={[
                      { title: 'Campo', dataIndex: 'nome', key: 'n', width: 200, render: (v) => <Text code style={{ color: '#020D7F' }}>{v}</Text> },
                      { title: 'Tipo', dataIndex: 'tipo', key: 't', width: 90 },
                      { title: 'Obrig.', dataIndex: 'obrigatorio', key: 'o', width: 70, render: (v) => v ? <Tag color="red">sim</Tag> : 'não' },
                      {
                        title: 'Descrição / opções', dataIndex: 'descricao', key: 'd',
                        render: (v, r) => (
                          <div>
                            {v}
                            {r.enum && r.enum.length > 0 && (
                              <div>
                                <Text type="secondary" style={{ fontSize: 10 }}>Valores: {r.enum.join(', ')}</Text>
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ]} />
                )}
              <Flex gap={16} style={{ fontSize: 11, paddingTop: 8, marginTop: 8, borderTop: '0.5px solid #f0f0f0' }}>
                <Text type="secondary">📊 <strong>{s.stats_30d.chamadas}</strong> chamadas em 30 dias</Text>
                {s.stats_30d.erros > 0 && <Text style={{ color: '#B91626' }}>⚠ {s.stats_30d.erros} erros ({s.stats_30d.taxa_erro_pct}%)</Text>}
                {s.stats_30d.ultimo_uso && <Text type="secondary">Último uso: {fmtDateTime(s.stats_30d.ultimo_uso)}</Text>}
              </Flex>
            </div>
          ),
        }))}
      />
    </div>
  );
}
